import { Context, MergeKey } from '@getaccept/editor-lib-new';
import { ref, watch, computed, defineComponent } from 'vue';
import { t } from '@getaccept/lib-shared-new/src/helpers/translation.helper';
import { SignatureHelper } from '@getaccept/editor-lib-new/src/helpers/signature.helper';
import { useInputFieldView } from '../composables/input-field-view.composable';
export default defineComponent({
    name: 'SignatureItem',
    props: {
        signature: {
            type: Object,
            default: () => ({}),
        },
        node: {
            type: Object,
            default: () => ({}),
        },
        mergeValues: { type: Object, required: true },
        recipient: { type: Object, required: true },
        isLoadingSignature: { type: Boolean, default: false },
        sender: { type: Object, default: () => ({}) },
        focusedRequiredId: { type: String, default: null },
        partiallySignedFields: { type: Array, default: () => [] },
        context: { type: String, default: null },
        isPdfViewer: { type: Boolean, default: null },
    },
    emits: ['update-loading-signature', 'open-signature-modal'],
    setup(props, { emit }) {
        const { getTooltip, getAssignedRecipient, getIsAssignedToCurrentUser } = useInputFieldView(props);
        const fieldHasFocus = ref(false);
        const fields = computed(() => { var _a; return ((_a = props.node) === null || _a === void 0 ? void 0 : _a.fields) || []; });
        const signDateSettings = computed(() => { var _a; return (_a = props.node) === null || _a === void 0 ? void 0 : _a.mergeTagsSettings.signDate; });
        const showMergeTagContainer = computed(() => getVisibleMergeTags(props.node.mergeTagsSettings).length || signDateSettings.value.show);
        const getBorderStyle = computed(() => { var _a; return (_a = props.node.borderStyle) === null || _a === void 0 ? void 0 : _a.toLowerCase(); });
        const getAlign = computed(() => { var _a; return (_a = props.node.align) === null || _a === void 0 ? void 0 : _a.toLowerCase(); });
        const getSignDate = computed(() => {
            if (!signedNode.value) {
                return props.isPdfViewer ? null : t('sign_date').toString();
            }
            return SignatureHelper.formatSignDate(signDateSettings.value.format, signedNode.value.tsaTime, props.mergeValues);
        });
        const signedNode = computed(() => SignatureHelper.hasPartiallySignedField(props.partiallySignedFields, props.signature.recipientId, props.signature.userId, props.node.id));
        watch(() => props.focusedRequiredId, () => {
            if (getIsAssignedToCurrentUser(props.signature) &&
                props.signature.id === props.focusedRequiredId) {
                fieldHasFocus.value = true;
            }
            else {
                fieldHasFocus.value = false;
            }
        });
        const getSignatureSvg = computed(() => {
            var _a, _b;
            if (props.signature.userId) {
                return props.context === Context.Document || props.isPdfViewer
                    ? (_a = props.mergeValues.sender) === null || _a === void 0 ? void 0 : _a.signature
                    : props.sender.signature;
            }
            if (signedNode.value) {
                emit('update-loading-signature');
                return (_b = SignatureHelper.getRecipient(props.mergeValues.recipients, props.signature.recipientId)) === null || _b === void 0 ? void 0 : _b.signature;
            }
            return null;
        });
        const getSignatureText = (name) => t('signature_display_here', { name }).toString();
        const getSignatureFieldText = (signature) => {
            var _a;
            if (getIsAssignedToCurrentUser(signature)) {
                return t('add_your_signature_here').toString();
            }
            return getSignatureText((_a = getAssignedRecipient(signature.recipientId)) === null || _a === void 0 ? void 0 : _a.fullName);
        };
        const handleClickSignField = (signature) => {
            if (props.isLoadingSignature || getSignatureSvg.value) {
                return;
            }
            if (getIsAssignedToCurrentUser(signature)) {
                emit('open-signature-modal');
            }
        };
        const isMergeTagEnabled = (mergeTagsSettings, mergeKey) => {
            if (mergeKey === MergeKey.signDate) {
                return false;
            }
            return mergeTagsSettings[mergeKey] === true;
        };
        const getVisibleMergeTags = (settings) => Object.keys(settings)
            .filter(key => isMergeTagEnabled(props.node.mergeTagsSettings, key))
            .map(key => {
            const mergeTag = props.signature.userId
                ? SignatureHelper.getMergeValueForSender(props.mergeValues, key)
                : SignatureHelper.getMergeValueForRecipient(props.mergeValues, key, props.signature.recipientId);
            return { key, value: mergeTag };
        });
        return {
            fields,
            getVisibleMergeTags,
            getTooltip,
            getIsAssignedToCurrentUser,
            getAssignedRecipient,
            getSignatureText,
            getSignatureFieldText,
            handleClickSignField,
            getSignatureSvg,
            getSignDate,
            fieldHasFocus,
            isMergeTagEnabled,
            signDateSettings,
            showMergeTagContainer,
            getBorderStyle,
            getAlign,
        };
    },
});
